<template>
    <h1 class="page-title">Arborescence des catégories</h1>
    <div v-if="isLoading" class="spinnerContainer">
        <ProgressSpinner
        aria-label="Chargement"
        :pt="{
            spinner: { style: { width: '50px', height: '50px', animationDuration: '.5s' } },
            circle: { style: { stroke: '#007bff', strokeWidth: 4, animation: '2s' } }
        }"
        />
    </div>
    <div v-else>
        <div class="toggleTreeItemsBtnContainer">
            <button type="button" class="btn btn-primary" @click="expandAll()">
                <i class="fas fa-plus-circle"></i>
                Dérouler la liste
            </button>
            <button type="button" class="btn btn-danger" @click="collapseAll()">
                <i class="fas fa-minus-circle"></i>
                Enrouler la liste
            </button>
        </div>
        <Tree :value="categories" :loading="isLoading" v-model:expandedKeys="expandedKeys">
            <template #default="slotProps">
                <b>{{ slotProps.node.label }}</b>
            </template>
            <template #url="slotProps">
                <a v-if="slotProps.node.children?.length" :href="slotProps.node.data">
                    {{ slotProps.node.label }}
                </a>
                <a v-else>
                    {{ slotProps.node.label }}
                </a>
            </template>
        </Tree>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from "vue";
import ApiService from "@/_services/api.service";
import swal from "../../../_helpers/swal";
import Tree from "primevue/tree";
import ProgressSpinner from "primevue/progressspinner";
import { useRouter } from "vue-router";

interface ICategory {
    key: string;
    label: string;
    data: string;
    type?: string;
    icon: string;
    expandedIcon?: string;
    collapsedIcon?: string;
    styleClass?: string;
    style?: string;
    children?: [] | ICategory[];
    leaf?: boolean;
}

const isLoading = ref<boolean>(true);
const categories = ref(Array<ICategory>());
const expandedKeys = ref({});
const collapsedIcon = "pi-plus";
const expandedIcon = "pi-minus";
const enableCategoryClass = "activeCategory";
const disableCategoryClass = "inactiveCategory";
const hasCategoryChildrenClass = "hasChildren";
const hasNotCategoryChildrenClass = "hasNotChildren";
const categoriesApiUrl = "categories";
const router = useRouter();

watch(
    categories,
    () => {
        isLoading.value = categories.value.length < 1;
    },
    { deep: true }
);

onMounted(async () => {
    categories.value = await fetchCategoriesData();
});

async function fetchCategoriesData() {
    ApiService.get(categoriesApiUrl, {
        params: { lvl: 1, "order[lft]": "ASC" },
    })
        .then(({ data }) => {
            if (data.data.length > 0) {
                data.data.forEach((item, index: number) => {
                    let styleClasses = [
                        true === item.enabled ? enableCategoryClass : disableCategoryClass,
                        item.children?.length > 0
                            ? hasCategoryChildrenClass
                            : hasNotCategoryChildrenClass,
                    ].join(" ");
                    let id = index.toString();
                    let newCategory: ICategory = {
                        key: id,
                        label: item.name,
                        data: item.children?.length
                            ? router.resolve({
                                name: "listCategories",
                                params: { uuid: item.uuid },
                            }).href
                            : "",
                        type: "url",
                        icon: "",
                        expandedIcon: expandedIcon,
                        collapsedIcon: collapsedIcon,
                        styleClass: styleClasses,
                        children: formatChildrenToCategory(item.children, id),
                        leaf: item.children?.length > 0,
                    };
                    categories.value.push(newCategory);
                });
            }
        })
        .catch((error) => {
            console.dir(error);
            swal.error();
            return false;
        });

    return categories.value;
}

function formatChildrenToCategory(data, parentKey: string) {
    let childrenData = Array<ICategory>();
    data.forEach((item, index: number) => {
        let styleClasses = [
            true === item.enabled ? enableCategoryClass : disableCategoryClass,
            item.children?.length > 0
                ? hasCategoryChildrenClass
                : hasNotCategoryChildrenClass,
        ].join(" ");
        let key = parentKey + "-" + index;
        let newCategory: ICategory = {
            key: key,
            label: item.name,
            data: item.children?.length
                ? router.resolve({
                    name: "listCategories",
                    params: { uuid: item.uuid },
                }).href
                : "",
            type: "url",
            icon: "",
            expandedIcon: expandedIcon,
            collapsedIcon: collapsedIcon,
            styleClass: styleClasses,
            children:
                item.children && item.children.length
                    ? formatChildrenToCategory(item.children, key)
                    : [],
            leaf: item.children?.length > 0,
        };
        childrenData.push(newCategory);
    });

    return childrenData;
}

function expandAll() {
    for (let category of categories.value) {
        expandNode(category);
    }

    expandedKeys.value = { ...expandedKeys.value };
}

function collapseAll() {
    expandedKeys.value = {};
}

function expandNode(category: ICategory) {
    if (category.children && category.children.length) {
        expandedKeys.value[category.key] = true;

        for (let child of category.children) {
            expandNode(child);
        }
    }
}
</script>

<style scoped lang="scss">

/* Temporary fix PrimeIcon lib not showing icon because of a bug https://github.com/primefaces/primeicons/pull/666/files */
.p-tree ::v-deep(.p-treenode[aria-expanded="true"] .hasChildren .p-tree-toggler-icon::before) {
    font-family: "primeicons" !important;
    content: "\e90f"; // - symbol to collapse
}

/* Temporary fix PrimeIcon lib not showing icon because of a bug https://github.com/primefaces/primeicons/pull/666/files */
.p-tree ::v-deep(.p-treenode[aria-expanded="false"] .hasChildren .p-tree-toggler-icon::before) {
    font-family: "primeicons" !important;
    content: "\e90d"; // + symbol to expan
}

.toggleTreeItemsBtnContainer {
    margin: 35px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;
    align-items: stretch;
    align-content: normal;
}

.p-tree ::v-deep(.inactiveCategory .p-treenode-label a) {
    color: #ff0000;
}

.p-tree ::v-deep(.p-treenode-label a) {
    color: #000;
}



.spinnerContainer {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80vh;
}
</style>